(() => {
  'use strict';

  if (customElements.get('mattress-selector')) return;

  class MattressSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.drawer = this.querySelector('[data-ms-drawer]');
    this.overlay = this.querySelector('[data-ms-overlay]');
    this.openButton = this.querySelector('[data-ms-open]');
    this.trigger = this.querySelector('[data-ms-trigger]');
    this.triggerDefault = this.querySelector('[data-ms-trigger-default]');
    this.triggerSelected = this.querySelector('[data-ms-trigger-selected]');
    this.summaryImage = this.querySelector('[data-ms-summary-image]');
    this.summaryTitle = this.querySelector('[data-ms-summary-title]');
    this.summaryVariant = this.querySelector('[data-ms-summary-variant]');
    this.summaryPrice = this.querySelector('[data-ms-summary-price]');
    this.summaryCompare = this.querySelector('[data-ms-summary-compare]');
    this.summarySave = this.querySelector('[data-ms-summary-save]');
    this.summaryDetailsButton = this.querySelector('[data-ms-summary-details]');
    this.summaryRemoveButton = this.querySelector('[data-ms-summary-remove]');
    this.summaryStatus = this.querySelector('[data-ms-summary-status]');
    this.summaryData = null;

    this.storageKey = `mattress-selector-selected:${
      window.Shopify?.shop || window.location.host
    }`;

    this.closeButtons = [
      ...this.querySelectorAll('[data-ms-close], [data-ms-close-all]')
    ];

    this.tabs = [...this.querySelectorAll('[data-ms-tab]')];
    this.panels = [...this.querySelectorAll('[data-ms-panel]')];
    this.cards = [...this.querySelectorAll('[data-ms-card]')];
    this.footer = this.querySelector('[data-ms-footer]');
    this.addButton = this.querySelector('[data-ms-add]');
    this.status = this.querySelector('[data-ms-status]');
    this.detailPanels = [...this.querySelectorAll('[data-ms-detail]')];
    this.activeDetail = null;
    this.selected = null;
    this.previousFocus = null;

    if (!this.drawer || !this.openButton || !this.addButton) return;

    this.openButton.addEventListener('click', () => this.open());

    this.overlay?.addEventListener('click', () => this.close());

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activateTab(tab.dataset.msTab, true);
      });
    });

    this.cards.forEach((card) => {
      const radio = card.querySelector('[data-ms-radio]');
      const selectArea = card.querySelector('.ms-card__select');

      /*
       * Do not rely only on the label -> hidden radio native behaviour.
       * Some mobile themes render duplicate responsive markup or use
       * transformed scroll containers, which can make the label target
       * the wrong hidden input.
       */
      selectArea?.addEventListener('click', (event) => {
        event.preventDefault();

        if (
          !radio ||
          radio.disabled ||
          card.dataset.available !== 'true'
        ) {
          return;
        }

        radio.checked = true;
        this.selectCard(card);
      });

      // Keep native keyboard/input changes working as a fallback.
      radio?.addEventListener('change', () => {
        if (radio.checked) {
          this.selectCard(card);
        }
      });
    });

    this.querySelectorAll('[data-ms-open-details]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.openDetails(button.dataset.msOpenDetails);
      });
    });

    this.querySelectorAll('[data-ms-details-back]').forEach((button) => {
      button.addEventListener('click', () => this.closeDetails());
    });

    this.querySelectorAll('[data-ms-variant-select]').forEach((select) => {
      select.addEventListener('change', () => {
        this.updateVariant(select);
      });
    });

    this.addButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      this.addSelectedToCart();
    });

    this.summaryDetailsButton?.addEventListener('click', () => {
      this.openSelectedDetails();
    });

    this.summaryRemoveButton?.addEventListener('click', () => {
      this.removeSelectedFromCart();
    });

    this.initGalleries();

    this.handleKeydown = this.handleKeydown.bind(this);

    document.addEventListener('keydown', this.handleKeydown);

    this.restoreSelectedSummary();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  open() {
    this.previousFocus = document.activeElement;
    this.dataset.open = 'true';

    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay?.setAttribute('aria-hidden', 'false');

    document.body.classList.add('ms-page-locked');

    requestAnimationFrame(() => {
      this.querySelector('[data-ms-close]')?.focus();
    });
  }

  close() {
    this.closeDetails(true);

    this.dataset.open = 'false';

    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay?.setAttribute('aria-hidden', 'true');

    document.body.classList.remove('ms-page-locked');

    if (this.previousFocus instanceof HTMLElement) {
      this.previousFocus.focus();
    }
  }

  activateTab(index, clearSelection) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.msTab === String(index);

      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.msPanel !== String(index);
    });

    if (clearSelection) {
      this.clearSelection();
    }
  }

  clearSelection() {
    this.cards.forEach((card) => {
      card.classList.remove('is-selected');

      const radio = card.querySelector('[data-ms-radio]');

      if (radio) {
        radio.checked = false;
      }
    });

    this.selected = null;
    this.footer.hidden = true;
    this.addButton.disabled = true;
    this.addButton.textContent =
      this.dataset.addText || 'Add Mattress';

    this.setStatus('');
  }

  selectCard(card) {
    const radio = card.querySelector('[data-ms-radio]');

    if (
      !radio ||
      radio.disabled ||
      card.dataset.available !== 'true'
    ) {
      return;
    }

    this.cards.forEach((item) => {
      const itemRadio = item.querySelector('[data-ms-radio]');
      const selected = item === card;

      item.classList.toggle('is-selected', selected);

      if (itemRadio) {
        itemRadio.checked = selected;
      }
    });

    this.selected = this.getCardSelection(card);

    this.footer.hidden = false;
    this.addButton.disabled = false;
    this.addButton.textContent =
      this.dataset.addText || 'Add Mattress';

    this.setStatus('');
  }

  openDetails(cardKey) {
    const panel = this.detailPanels.find(
      (item) => item.dataset.msDetail === cardKey
    );

    if (!panel) return;

    this.closeDetails(true);

    this.activeDetail = panel;

    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');

    const card = this.cards.find(
      (item) => item.dataset.cardKey === cardKey
    );

    const select = panel.querySelector('[data-ms-variant-select]');

    if (card && select && card.dataset.currentVariant) {
      select.value = card.dataset.currentVariant;
    }

    requestAnimationFrame(() => {
      panel.classList.add('is-active');
      panel.querySelector('[data-ms-details-back]')?.focus();
    });
  }

  closeDetails(immediate = false) {
    if (!this.activeDetail) return;

    const panel = this.activeDetail;

    panel.classList.remove('is-active');
    panel.setAttribute('aria-hidden', 'true');

    this.activeDetail = null;

    if (immediate) {
      panel.hidden = true;
      return;
    }

    window.setTimeout(() => {
      if (!panel.classList.contains('is-active')) {
        panel.hidden = true;
      }
    }, 280);
  }

  updateVariant(select) {
    const panel = select.closest('[data-ms-detail]');
    const option = select.selectedOptions[0];

    if (!panel || !option) return;

    const cardKey = panel.dataset.cardKey;

    const card = this.cards.find(
      (item) => item.dataset.cardKey === cardKey
    );

    if (!card) return;

    const variantId = option.value;
    const variantTitle = option.dataset.title || '';
    const available = option.dataset.available === 'true';
    const price = this.cleanDisplayText(option.dataset.price || '');
    const image =
      option.dataset.image ||
      card.dataset.productImage ||
      '';

    const compare = this.cleanDisplayText(
      option.dataset.compare || ''
    );

    const save = this.cleanDisplayText(
      option.dataset.save || ''
    );

    const saveAmount = Number(option.dataset.saveAmount || 0);

    card.dataset.currentVariant = variantId;
    card.dataset.currentVariantTitle = variantTitle;
    card.dataset.currentPrice = price;
    card.dataset.currentCompare = compare;
    card.dataset.currentSave = save;
    card.dataset.currentSaveAmount = String(saveAmount);
    card.dataset.currentImage = image;
    card.dataset.available = String(available);

    const radio = card.querySelector('[data-ms-radio]');

    if (radio) {
      radio.value = variantId;
      radio.disabled = !available;
    }

    const cardPrice = card.querySelector('[data-ms-card-price]');

    if (cardPrice) {
      cardPrice.textContent = price;
    }

    const cardCompare = card.querySelector(
      '[data-ms-card-compare]'
    );

    if (cardCompare) {
      cardCompare.textContent = compare;
      cardCompare.hidden = !compare;
    }

    const cardSave = card.querySelector('[data-ms-card-save]');

    if (cardSave) {
      cardSave.textContent = `${
        this.dataset.savePrefix || 'SAVE:'
      } ${save}`.trim();

      cardSave.hidden = saveAmount <= 0;
    }

    const detailPrice = panel.querySelector(
      '[data-ms-detail-price]'
    );

    if (detailPrice) {
      detailPrice.textContent = price;
    }

    const detailCompare = panel.querySelector(
      '[data-ms-detail-compare]'
    );

    if (detailCompare) {
      detailCompare.textContent = compare;
      detailCompare.hidden = !compare;
    }

    if (this.selected?.cardKey === cardKey) {
      this.selected = this.getCardSelection(card);
      this.addButton.disabled = !available;
    }
  }

  getCardSelection(card) {
    return {
      cardKey: card.dataset.cardKey || '',
      productId: card.dataset.productId || '',
      variantId: card.dataset.currentVariant || '',
      productTitle: card.dataset.productTitle || '',
      variantTitle: card.dataset.currentVariantTitle || '',
      price: this.cleanDisplayText(
        card.dataset.currentPrice || ''
      ),
      compare: this.cleanDisplayText(
        card.dataset.currentCompare || ''
      ),
      save: this.cleanDisplayText(
        card.dataset.currentSave || ''
      ),
      saveAmount: Number(
        card.dataset.currentSaveAmount || 0
      ),
      image:
        card.dataset.currentImage ||
        card.dataset.productImage ||
        '',
      lineKey: ''
    };
  }

  showSelectedSummary(data, persist = false) {
    if (
      !data?.variantId ||
      !this.triggerSelected ||
      !this.triggerDefault
    ) {
      return;
    }

    const cleanPrice = this.cleanDisplayText(data.price || '');
    const cleanCompare = this.cleanDisplayText(
      data.compare || ''
    );
    const cleanSave = this.cleanDisplayText(data.save || '');

    this.summaryData = {
      ...data,
      price: cleanPrice,
      compare: cleanCompare,
      save: cleanSave
    };

    if (this.summaryTitle) {
      this.summaryTitle.textContent = data.productTitle || '';
    }

    if (this.summaryPrice) {
      this.summaryPrice.textContent = cleanPrice;
    }

    if (this.summaryVariant) {
      const variantTitle = data.variantTitle || '';

      const showVariant =
        variantTitle &&
        variantTitle.toLowerCase() !== 'default title';

      this.summaryVariant.textContent = showVariant
        ? variantTitle
        : '';

      this.summaryVariant.hidden = !showVariant;
    }

    if (this.summaryCompare) {
      this.summaryCompare.textContent = cleanCompare;
      this.summaryCompare.hidden = !cleanCompare;
    }

    if (this.summarySave) {
      const saveAmount = Number(data.saveAmount || 0);

      this.summarySave.textContent = `${
        this.dataset.savePrefix || 'SAVE:'
      } ${cleanSave}`.trim();

      this.summarySave.hidden =
        saveAmount <= 0 || !cleanSave;
    }

    if (this.summaryImage) {
      if (data.image) {
        this.summaryImage.src = data.image;
        this.summaryImage.alt =
          data.productTitle || 'Selected mattress';

        this.summaryImage.hidden = false;
      } else {
        this.summaryImage.removeAttribute('src');
        this.summaryImage.alt = '';
        this.summaryImage.hidden = true;
      }
    }

    if (this.summaryStatus) {
      this.summaryStatus.textContent = '';
    }

    if (this.summaryRemoveButton) {
      this.summaryRemoveButton.disabled = false;
      this.summaryRemoveButton.removeAttribute('aria-busy');
      this.summaryRemoveButton.textContent = 'Remove';
    }

    this.triggerDefault.hidden = true;
    this.triggerSelected.hidden = false;

    this.trigger?.classList.add('has-selected');

    if (persist) {
      try {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify(this.summaryData)
        );
      } catch (error) {
        console.warn(
          'Selected mattress could not be saved locally.',
          error
        );
      }
    }
  }

  clearSelectedSummary() {
    this.summaryData = null;

    this.triggerDefault?.removeAttribute('hidden');

    if (this.triggerSelected) {
      this.triggerSelected.hidden = true;
    }

    this.trigger?.classList.remove('has-selected');

    if (this.summaryStatus) {
      this.summaryStatus.textContent = '';
    }

    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn(
        'Selected mattress storage could not be cleared.',
        error
      );
    }
  }

  openSelectedDetails() {
    const data = this.summaryData;

    this.open();

    if (!data?.cardKey) return;

    window.requestAnimationFrame(() => {
      const cardExists = this.cards.some(
        (card) => card.dataset.cardKey === data.cardKey
      );

      if (cardExists) {
        this.openDetails(data.cardKey);
      }
    });
  }

  async removeSelectedFromCart() {
    const data = this.summaryData;

    if (
      !data?.variantId ||
      !this.summaryRemoveButton ||
      this.summaryRemoveButton.disabled
    ) {
      return;
    }

    const button = this.summaryRemoveButton;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Removing...';

    if (this.summaryStatus) {
      this.summaryStatus.textContent = '';
    }

    try {
      const root = window.Shopify?.routes?.root || '/';

      let lineKey = data.lineKey || '';

      /*
       * If line key was not saved, read the current cart and find
       * the matching variant.
       */
      if (!lineKey) {
        const cartResponse = await fetch(`${root}cart.js`, {
          headers: {
            Accept: 'application/json'
          }
        });

        if (!cartResponse.ok) {
          throw new Error('Unable to read the cart.');
        }

        const currentCart = await cartResponse.json();

        const item = currentCart.items?.find((cartItem) => {
          const itemVariantId =
            cartItem.variant_id || cartItem.id;

          return (
            Number(itemVariantId) === Number(data.variantId)
          );
        });

        lineKey = item?.key || '';
      }

      /*
       * Item is already absent from the cart.
       */
      if (!lineKey) {
        this.clearSelectedSummary();
        this.clearSelection();

        document.dispatchEvent(
          new CustomEvent('cart:build')
        );

        return;
      }

      /*
       * Remove the exact Shopify cart line through AJAX.
       */
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      const cart = await response.json();

      if (!response.ok) {
        throw new Error(
          cart.description ||
          cart.message ||
          'Unable to remove this mattress.'
        );
      }

      /*
       * Immediately update the visible selector and cart count.
       */
      this.updateCartCount(cart.item_count);
      this.clearSelectedSummary();
      this.clearSelection();

      /*
       * IMPORTANT:
       * Impulse listens for cart:build and uses CartForm.buildCart()
       * to AJAX-fetch and redraw cart drawer markup, subtotal,
       * discounts, quantities and empty-cart state.
       */
      document.dispatchEvent(
        new CustomEvent('cart:build')
      );

      /*
       * Keep the general cart updated event for other integrations.
       */
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart,
            source: 'mattress-selector',
            action: 'remove'
          }
        })
      );

      /*
       * Custom removal event for any additional custom scripts.
       */
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:removed', {
          bubbles: true,
          detail: {
            cart,
            variantId: data.variantId,
            source: 'mattress-selector'
          }
        })
      );
    } catch (error) {
      if (this.summaryStatus) {
        this.summaryStatus.textContent =
          error.message ||
          'Unable to remove this mattress.';
      }

      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = 'Remove';
    }
  }

  async restoreSelectedSummary() {
    let saved = null;

    try {
      saved = JSON.parse(
        localStorage.getItem(this.storageKey) || 'null'
      );
    } catch (error) {
      console.warn(
        'Selected mattress storage is unavailable.',
        error
      );

      return;
    }

    if (!saved?.variantId) return;

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return;

      const cart = await response.json();

      const item = cart.items?.find((cartItem) => {
        const itemVariantId =
          cartItem.variant_id || cartItem.id;

        return (
          Number(itemVariantId) ===
          Number(saved.variantId)
        );
      });

      if (!item) {
        this.clearSelectedSummary();
        return;
      }

      this.showSelectedSummary(
        {
          ...saved,
          lineKey: item.key || saved.lineKey || '',
          productTitle:
            item.product_title || saved.productTitle,
          variantTitle:
            item.variant_title || saved.variantTitle,
          image: item.image || saved.image
        },
        true
      );
    } catch (error) {
      console.warn(
        'Selected mattress could not be restored from the cart.',
        error
      );
    }
  }

  async addSelectedToCart() {
    if (
      !this.selected?.variantId ||
      this.addButton.disabled
    ) {
      return;
    }

    const defaultText =
      this.dataset.addText || 'Add Mattress';

    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');

    this.addButton.textContent =
      this.dataset.addingText || 'Adding...';

    this.setStatus('');

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(this.selected.variantId),
              quantity: 1
            }
          ]
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Unable to add this mattress.'
        );
      }

      let cart = null;

      try {
        const cartResponse = await fetch(`${root}cart.js`, {
          headers: {
            Accept: 'application/json'
          }
        });

        if (!cartResponse.ok) {
          throw new Error('Unable to refresh the cart.');
        }

        cart = await cartResponse.json();

        this.updateCartCount(cart.item_count);

        const addedItem = cart.items?.find((cartItem) => {
          const itemVariantId =
            cartItem.variant_id || cartItem.id;

          return (
            Number(itemVariantId) ===
            Number(this.selected.variantId)
          );
        });

        if (addedItem?.key) {
          this.selected.lineKey = addedItem.key;
        }
      } catch (cartError) {
        console.warn(
          'Mattress added, but cart count could not be refreshed.',
          cartError
        );
      }

      this.showSelectedSummary(this.selected, true);

      this.addButton.textContent =
        this.dataset.addedText || 'Added';

      this.setStatus(
        this.dataset.addedText || 'Added to cart'
      );

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart,
            source: 'mattress-selector',
            action: 'add'
          }
        })
      );

      /*
       * Impulse cart drawer already listens for ajaxProduct:added.
       * It rebuilds and opens the cart drawer.
       */
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: {
            item: result,
            product: result,
            cart,
            source: 'mattress-selector'
          }
        })
      );

      if (this.dataset.closeAfterAdd === 'true') {
        window.setTimeout(() => {
          this.close();

          this.addButton.textContent = defaultText;
          this.addButton.disabled = false;

          this.addButton.removeAttribute('aria-busy');
        }, 700);
      } else {
        window.setTimeout(() => {
          this.addButton.textContent = defaultText;
          this.addButton.disabled = false;

          this.addButton.removeAttribute('aria-busy');
        }, 1400);
      }
    } catch (error) {
      this.setStatus(
        error.message ||
        'Unable to add this mattress.'
      );

      this.addButton.textContent = defaultText;
      this.addButton.disabled = false;

      this.addButton.removeAttribute('aria-busy');
    }
  }

  cleanDisplayText(value) {
    const source = String(value || '').trim();

    if (!source) return '';

    /*
     * Shopify money formats can contain HTML, for example:
     * <span class="money">£145.00</span>.
     * Decode entities first, then return only visible text.
     */
    const decoder = document.createElement('textarea');

    decoder.innerHTML = source;

    const container = document.createElement('div');

    container.innerHTML = decoder.value;

    return (container.textContent || '').trim();
  }

  updateCartCount(itemCount) {
    const count = Number(itemCount || 0);

    /*
     * Impulse theme selectors plus common Shopify selectors.
     */
    const numberSelectors = [
      '[data-cart-count]',
      '.cart-link__bubble-num',
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble span:first-child'
    ];

    document
      .querySelectorAll(numberSelectors.join(','))
      .forEach((node) => {
        node.textContent = String(count);
      });

    /*
     * Impulse cart bubble visibility.
     */
    document
      .querySelectorAll('.cart-link__bubble')
      .forEach((bubble) => {
        bubble.classList.toggle(
          'cart-link__bubble--visible',
          count > 0
        );
      });

    /*
     * Dawn/common Shopify bubble visibility fallback.
     */
    document
      .querySelectorAll('.cart-count-bubble')
      .forEach((bubble) => {
        bubble.hidden = count <= 0;
      });
  }

  setStatus(message) {
    if (this.status) {
      this.status.textContent = message;
    }
  }

  initGalleries() {
    this.querySelectorAll('[data-ms-gallery]').forEach(
      (gallery) => {
        const track = gallery.querySelector(
          '[data-ms-gallery-track]'
        );

        const slides = [
          ...gallery.querySelectorAll('.ms-gallery__slide')
        ];

        const dots = [
          ...gallery.querySelectorAll(
            '[data-ms-gallery-dot]'
          )
        ];

        const prev = gallery.querySelector(
          '[data-ms-gallery-prev]'
        );

        const next = gallery.querySelector(
          '[data-ms-gallery-next]'
        );

        let index = 0;

        if (!track || slides.length <= 1) return;

        const render = () => {
          index = Math.max(
            0,
            Math.min(index, slides.length - 1)
          );

          track.style.transform =
            `translateX(-${index * 100}%)`;

          dots.forEach((dot, dotIndex) => {
            dot.classList.toggle(
              'is-active',
              dotIndex === index
            );
          });
        };

        prev?.addEventListener('click', () => {
          index =
            index === 0
              ? slides.length - 1
              : index - 1;

          render();
        });

        next?.addEventListener('click', () => {
          index =
            index === slides.length - 1
              ? 0
              : index + 1;

          render();
        });

        dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            index = Number(
              dot.dataset.msGalleryDot || 0
            );

            render();
          });
        });
      }
    );
  }

  handleKeydown(event) {
    if (this.dataset.open !== 'true') return;

    if (event.key === 'Escape') {
      if (this.activeDetail) {
        this.closeDetails();
      } else {
        this.close();
      }

      return;
    }

    if (event.key !== 'Tab') return;

    const scope = this.activeDetail || this.drawer;

    const focusable = [
      ...scope.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ].filter((element) => {
      return (
        !element.hidden &&
        element.offsetParent !== null
      );
    });

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  }
}


  /*
   * FIX (main buy-button conflict):
   * This fallback IS required for the main product form — without it,
   * the browser performs a normal (non-AJAX) submit to /cart/add,
   * which navigates the page instead of opening the cart drawer.
   *
   * The earlier bug was that this function overwrote the button's
   * text with a hardcoded "Adding..." label, which replaced the
   * theme's own loading-spinner visual. That text-swap has been
   * removed below — we now only toggle `disabled` and `aria-busy`
   * on the button, which most themes (including Impulse) use to
   * drive their own spinner styling via CSS.
   */
  function installProductFormFallback() {
    if (window.__msProductFormFallbackInstalled) return;

    window.__msProductFormFallbackInstalled = true;

    document.addEventListener('submit', async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      if (
        !form.matches(
          'form.product-single__form[action*="/cart/add"]'
        )
      ) {
        return;
      }

      const submitter = event.submitter;

      if (
        submitter instanceof HTMLElement &&
        !submitter.matches('[name="add"], [data-add-to-cart]')
      ) {
        return;
      }

      /*
       * If Impulse has already intercepted the form, leave it alone.
       * This fallback only handles product forms that would otherwise
       * perform a normal page navigation.
       */
      if (event.defaultPrevented) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (form.dataset.msProductAdding === 'true') return;

      form.dataset.msProductAdding = 'true';

      const button =
        submitter instanceof HTMLButtonElement
          ? submitter
          : form.querySelector('[data-add-to-cart]');

      // NOTE: buttonText is intentionally left untouched now —
      // we no longer overwrite the label with "Adding...".
      // If you want your own spinner class toggled instead of
      // relying on [aria-busy], add it here, e.g.:
      // button?.classList.add('is-loading');

      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }

      try {
        const root = window.Shopify?.routes?.root || '/';
        const formData = new FormData(form);

        /*
         * These fields can force a normal checkout redirect when a form
         * is submitted by an app or custom product template. They are not
         * needed for an AJAX add-to-cart request.
         */
        formData.delete('return_to');
        formData.delete('checkout');

        if (!formData.get('quantity')) {
          formData.set('quantity', '1');
        }

        if (!formData.get('id')) {
          throw new Error('Please select an available product option.');
        }

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const product = await response.json();

        if (!response.ok) {
          throw new Error(
            product.description ||
              product.message ||
              'Unable to add this product to the cart.'
          );
        }

        let cart = null;

        try {
          const cartResponse = await fetch(`${root}cart.js`, {
            headers: {
              Accept: 'application/json'
            }
          });

          if (cartResponse.ok) {
            cart = await cartResponse.json();
          }
        } catch (cartError) {
          console.warn(
            'Product added, but the cart could not be refreshed.',
            cartError
          );
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart,
              source: 'product-form-fallback',
              action: 'add'
            }
          })
        );

        /*
         * Impulse listens for this event to rebuild and open its cart
         * drawer after an AJAX product addition.
         */
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: {
              product,
              item: product,
              cart,
              source: 'product-form-fallback'
            }
          })
        );
      } catch (error) {
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:error', {
            bubbles: true,
            detail: {
              errorMessage:
                error.message ||
                'Unable to add this product to the cart.'
            }
          })
        );

        console.error('Product add-to-cart fallback failed.', error);
      } finally {
        delete form.dataset.msProductAdding;

        window.setTimeout(() => {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            // button?.classList.remove('is-loading');
          }
        }, 800);
      }
    });
  }

  installProductFormFallback();

  customElements.define(
    'mattress-selector',
    MattressSelector
  );
})();